/**
 * Admin — accès offerts (migration 0306) : logique PURE de la page
 * « Utilisateurs » (aperçu du message, durées, résumé des accès).
 *
 * ⚠️ Miroir de `supabase/functions/_shared/admin-gift.ts` (le serveur rend le
 * message envoyé avec la vraie date de fin) — parité testée côté `_shared`.
 */

export type GiftAccessType = 'normal' | 'epreuves';

export const GIFT_ACCESS_OPTIONS: { value: GiftAccessType[]; label: string; hint: string }[] = [
  { value: ['normal'], label: 'Accès complet', hint: 'Exercices, cours et SKILLCARDS' },
  { value: ['epreuves'], label: 'Pass Épreuves', hint: 'Annales et sujets d’examen' },
  { value: ['normal', 'epreuves'], label: 'Les deux', hint: 'Accès complet + Épreuves' },
];

export const GIFT_DURATION_PRESETS: { days: number; label: string }[] = [
  { days: 1, label: '24 h' },
  { days: 7, label: '7 jours' },
  { days: 30, label: '30 jours' },
  { days: 90, label: '3 mois' },
  { days: 365, label: '1 an' },
];

export const GIFT_MAX_DAYS = 3650;
export const GIFT_TITLE_MAX = 80;
export const GIFT_BODY_MAX = 1000;

export const DEFAULT_GIFT_TITLE = 'Un cadeau de l’équipe JeRaisonne 🎁';
export const DEFAULT_GIFT_TEMPLATE =
  'Bonne nouvelle {prenom} ! L’équipe JeRaisonne t’offre {acces} pendant {duree}.\n\n' +
  'Ton accès est actif dès maintenant, jusqu’au {date_fin}. Bon entraînement !';

const DAY_MS = 24 * 60 * 60 * 1000;

export function computeGiftExpiry(
  currentUntil: string | null | undefined,
  now: Date,
  days: number,
  extend = true,
): Date {
  const current = currentUntil ? new Date(currentUntil).getTime() : Number.NaN;
  const start =
    extend && Number.isFinite(current) && current > now.getTime() ? current : now.getTime();
  return new Date(start + days * DAY_MS);
}

export function giftAccessLabel(types: readonly GiftAccessType[]): string {
  const hasNormal = types.includes('normal');
  const hasEpreuves = types.includes('epreuves');
  if (hasNormal && hasEpreuves) return 'un accès complet à JeRaisonne et le pass Épreuves';
  if (hasEpreuves) return 'le pass Épreuves';
  return 'un accès complet à JeRaisonne';
}

export function giftDurationLabel(days: number): string {
  if (days >= 365 && days % 365 === 0) {
    const y = days / 365;
    return y === 1 ? '1 an' : `${y} ans`;
  }
  if (days >= 60 && days % 30 === 0) return `${days / 30} mois`;
  return days === 1 ? '1 jour' : `${days} jours`;
}

export function formatGiftDate(d: Date): string {
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(d);
}

export function giftFirstName(displayName: string | null | undefined): string {
  const first = (displayName ?? '').trim().split(/\s+/)[0];
  return first || 'toi';
}

export interface GiftMessageVars {
  prenom: string;
  acces: string;
  duree: string;
  date_fin: string;
}

export function renderGiftMessage(template: string, vars: GiftMessageVars): string {
  return template.replace(
    /\{(prenom|acces|duree|date_fin)\}/g,
    (_, k: keyof GiftMessageVars) => vars[k],
  );
}

/** Fin la plus lointaine parmi les types offerts (date affichée dans le message). */
export function giftEndDate(
  types: readonly GiftAccessType[],
  current: { normal: string | null; epreuves: string | null },
  now: Date,
  days: number,
  extend = true,
): Date {
  return types
    .map((t) => computeGiftExpiry(current[t], now, days, extend))
    .reduce((a, b) => (b > a ? b : a));
}

/** « 12 », « 0 », « 4000 » → entier valide 1..GIFT_MAX_DAYS, sinon null. */
export function parseGiftDays(v: string): number | null {
  if (!/^\d{1,4}$/.test(v.trim())) return null;
  const n = Number(v.trim());
  return n >= 1 && n <= GIFT_MAX_DAYS ? n : null;
}

// ── Liste des utilisateurs ──────────────────────────────────────────────────

export interface AdminUserRow {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  role: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  /** Connexion, session rafraîchie (app ouverte) ou exercice joué — le plus récent. */
  last_activity_at: string | null;
  level: string | null;
  has_subscription: boolean;
  normal_until: string | null;
  epreuves_until: string | null;
  deck_until: string | null;
}

export type AdminUserSort = 'activity' | 'created' | 'name';

export const ADMIN_USER_SORTS: { value: AdminUserSort; label: string }[] = [
  { value: 'activity', label: 'Dernière activité' },
  { value: 'created', label: 'Inscription récente' },
  { value: 'name', label: 'Nom (A → Z)' },
];

/** « à l'instant », « il y a 5 min », « il y a 3 h », « hier », « il y a 4 j », sinon la date. */
export function formatLastActivity(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return 'Jamais actif';
  const diff = now.getTime() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'Actif à l’instant';
  if (min < 60) return `Actif il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `Actif il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'Actif hier';
  if (d < 30) return `Actif il y a ${d} j`;
  return `Actif le ${new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Paris',
  }).format(new Date(iso))}`;
}

export const ROLE_LABELS: Record<string, string> = {
  student: 'Élève',
  parent: 'Parent',
  tutor: 'Professeur',
  admin: 'Admin',
  player: 'Curieux',
};

export type AccessBadge = { label: string; tone: 'success' | 'primary' | 'accent' | 'neutral' };

/** Pastilles d'accès d'une ligne (« Abonnement », « Accès jusqu'au … »…). */
export function userAccessBadges(row: AdminUserRow, now: Date = new Date()): AccessBadge[] {
  const out: AccessBadge[] = [];
  const short = (iso: string) =>
    new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      timeZone: 'Europe/Paris',
    }).format(new Date(iso));
  if (row.has_subscription) out.push({ label: 'Abonnement', tone: 'success' });
  if (row.normal_until && new Date(row.normal_until) > now) {
    out.push({ label: `Accès → ${short(row.normal_until)}`, tone: 'primary' });
  }
  if (row.epreuves_until && new Date(row.epreuves_until) > now) {
    out.push({ label: `Épreuves → ${short(row.epreuves_until)}`, tone: 'accent' });
  }
  if (row.deck_until && new Date(row.deck_until) > now) {
    out.push({ label: 'SKILLCARDS achetées', tone: 'neutral' });
  }
  return out;
}

export const PASS_SOURCE_LABELS: Record<string, string> = {
  stripe: 'Payé (Stripe)',
  parent_payment: 'Payé par un parent',
  iap_ios: 'Achat iOS',
  iap_android: 'Achat Android',
  manual_test: 'Compte de test',
  admin_gift: 'Offert',
};
